import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Calendar, Filter, MessageSquare, Plus, Search, Users } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  getClientDisplayName,
  InteractionPriorityText,
  InteractionStatusBadge,
  interactionPriorityLabels as priorityLabels,
  interactionStatusLabels as statusLabels,
  interactionTypeLabels,
  type InteractionPriority,
  type InteractionStatus,
  type InteractionType
} from '@/components/interactions/InteractionBadges'
import { clientService } from '../services/client-service'
import { Client, ClientInteraction } from '../types/client'
import { useDateTimeFormat } from '../hooks/useDateTimeFormat'
import { NativeSelect } from "@/components/ui/native-select"

import { getErrorMessage } from '../utils/error.utils'
import { parseDbTimestamp } from '../utils/datetime'
import { DataTable } from "@/components/ui/data-table"

interface InteractionWithClient extends Omit<ClientInteraction, 'tags'> {
  tags: string[]
  client?: Client
}

export function InteractionsPage() {
  const navigate = useNavigate()
  const { formatDateOnly } = useDateTimeFormat()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [interactions, setInteractions] = useState<InteractionWithClient[]>([])
  const [filteredInteractions, setFilteredInteractions] = useState<InteractionWithClient[]>([])

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<InteractionType | ''>('')
  const [filterStatus, setFilterStatus] = useState<InteractionStatus | ''>('')
  const [filterPriority, setFilterPriority] = useState<InteractionPriority | ''>('')
  const [filterDateFrom, setFilterDateFrom] = useState('')
  const [filterDateTo, setFilterDateTo] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  const itemsPerPage = 20

  useEffect(() => {
    loadAllInteractions()
  }, [])

  const loadAllInteractions = async () => {
    try {
      setLoading(true)
      setError(null)

      // Buscar todos os clientes e suas interações
      const clientsResponse = await clientService.getAllClients({}, { page: 1, limit: 1000, sort: 'createdAt:desc' })

      const allInteractionsPromises = clientsResponse.clients.map(async (client) => {
        const clientInteractions = await clientService.getClientInteractions(client.id)
        return clientInteractions.map((interaction: ClientInteraction): InteractionWithClient => ({
          ...interaction,
          tags: interaction.tags ?? [],
          client
        }))
      })

      const allInteractionsArrays = await Promise.all(allInteractionsPromises)
      const allInteractions = allInteractionsArrays.flat()

      // Ordenar por data de criação (mais recente primeiro)
      allInteractions.sort((a, b) => {
        const bTime = parseDbTimestamp(b.createdAt)?.getTime() ?? 0
        const aTime = parseDbTimestamp(a.createdAt)?.getTime() ?? 0
        return bTime - aTime
      })

      setInteractions(allInteractions)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar atendimentos'))
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = useCallback(() => {
    let filtered = [...interactions]

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(interaction => {
        const clientName = getClientDisplayName(interaction.client)
        return (
          interaction.subject.toLowerCase().includes(searchLower) ||
          interaction.description?.toLowerCase().includes(searchLower) ||
          clientName.toLowerCase().includes(searchLower) ||
          interaction.tags.some(tag => tag.toLowerCase().includes(searchLower))
        )
      })
    }

    // Type filter
    if (filterType) {
      filtered = filtered.filter(i => i.type === filterType)
    }

    // Status filter
    if (filterStatus) {
      filtered = filtered.filter(i => i.status === filterStatus)
    }

    // Priority filter
    if (filterPriority) {
      filtered = filtered.filter(i => i.priority === filterPriority)
    }

    // Date range filter
    if (filterDateFrom) {
      const fromDate = new Date(filterDateFrom)
      filtered = filtered.filter(i => {
        const interactionDate = parseDbTimestamp(i.scheduledFor ?? i.createdAt)
        return interactionDate ? interactionDate >= fromDate : false
      })
    }

    if (filterDateTo) {
      const toDate = new Date(filterDateTo)
      toDate.setHours(23, 59, 59, 999)
      filtered = filtered.filter(i => {
        const interactionDate = parseDbTimestamp(i.scheduledFor ?? i.createdAt)
        return interactionDate ? interactionDate <= toDate : false
      })
    }

    setFilteredInteractions(filtered)
  }, [interactions, searchTerm, filterType, filterStatus, filterPriority, filterDateFrom, filterDateTo])

  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const clearFilters = () => {
    setSearchTerm('')
    setFilterType('')
    setFilterStatus('')
    setFilterPriority('')
    setFilterDateFrom('')
    setFilterDateTo('')
  }

  const columns = useMemo<ColumnDef<InteractionWithClient>[]>(() => [
    {
      id: 'contato',
      header: 'Contato',
      accessorFn: (i) => getClientDisplayName(i.client),
      cell: ({ row }) => {
        const interaction = row.original
        return (
          <div className="flex min-w-0 items-center gap-2">
            <Users className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{getClientDisplayName(interaction.client)}</div>
              <div className="text-xs text-muted-foreground">#{interaction.client?.clientNumber || 'N/A'}</div>
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'subject',
      header: 'Assunto',
      cell: ({ row }) => {
        const interaction = row.original
        return (
          <div className="min-w-0">
            <div className="text-sm">{interaction.subject}</div>
            {interaction.tags.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {interaction.tags.slice(0, 2).map((tag, idx) => (
                  <Badge key={idx} variant="secondary">{tag}</Badge>
                ))}
                {interaction.tags.length > 2 && (
                  <span className="text-xs text-muted-foreground">+{interaction.tags.length - 2}</span>
                )}
              </div>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'type',
      header: 'Tipo',
      cell: ({ row }) => <span className="text-sm">{interactionTypeLabels[row.original.type]}</span>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <InteractionStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'priority',
      header: 'Prioridade',
      cell: ({ row }) => <InteractionPriorityText priority={row.original.priority} />,
    },
    {
      id: 'data',
      header: 'Data',
      accessorFn: (i) => i.scheduledFor || i.createdAt,
      cell: ({ row }) => {
        const interaction = row.original
        return (
          <span className="text-sm tabular-nums text-muted-foreground">
            {interaction.scheduledFor ? formatDateOnly(interaction.scheduledFor) : formatDateOnly(interaction.createdAt)}
          </span>
        )
      },
    },
  ], [formatDateOnly])

  const hasFilters = Boolean(searchTerm || filterType || filterStatus || filterPriority || filterDateFrom || filterDateTo)
  const activeFilterCount = [filterType, filterStatus, filterPriority, filterDateFrom, filterDateTo].filter(Boolean).length

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <span className="sr-only">Carregando atendimentos...</span>
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-10" />
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14" />)}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <MessageSquare className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Atendimentos</h1>
            <Badge variant="secondary">{filteredInteractions.length} {filteredInteractions.length === 1 ? 'atendimento' : 'atendimentos'}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">Gerencie todos os atendimentos registrados</p>
        </div>
        <Button asChild>
          <Link to="/interactions/new"><Plus className="size-4" /> Novo Atendimento</Link>
        </Button>
      </div>

      {error && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
          <div>
            <p className="font-medium text-destructive">Erro ao carregar atendimentos</p>
            <p className="text-muted-foreground">{error}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => loadAllInteractions()}>Tentar novamente</Button>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              type="text"
              aria-label="Buscar atendimentos"
              placeholder="Buscar por assunto, descrição, contato ou tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)} aria-expanded={showFilters}>
              <Filter className="size-4" />
              Filtros
              {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
            </Button>
            {hasFilters && <Button variant="ghost" onClick={clearFilters}>Limpar filtros</Button>}
          </div>
        </div>

        {showFilters && (
          <Card size="sm">
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="interactions-filter-type">Tipo</Label>
                <NativeSelect
                  id="interactions-filter-type"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as InteractionType | '')}
                >
                  <option value="">Todos</option>
                  {Object.entries(interactionTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </NativeSelect>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="interactions-filter-status">Status</Label>
                <NativeSelect
                  id="interactions-filter-status"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as InteractionStatus | '')}
                >
                  <option value="">Todos</option>
                  {Object.entries(statusLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </NativeSelect>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="interactions-filter-priority">Prioridade</Label>
                <NativeSelect
                  id="interactions-filter-priority"
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value as InteractionPriority | '')}
                >
                  <option value="">Todas</option>
                  {Object.entries(priorityLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </NativeSelect>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="interactions-filter-from">Data Inicial</Label>
                <Input
                  id="interactions-filter-from"
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="interactions-filter-to">Data Final</Label>
                <Input
                  id="interactions-filter-to"
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {filteredInteractions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <Calendar className="size-10 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-sm font-medium">Nenhum atendimento encontrado</h3>
            <p className="text-sm text-muted-foreground">
              {interactions.length === 0
                ? 'Comece criando seu primeiro atendimento'
                : 'Tente ajustar os filtros de busca'}
            </p>
            {interactions.length === 0 && (
              <Button asChild className="mt-2">
                <Link to="/interactions/new"><Plus className="size-4" /> Novo Atendimento</Link>
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="min-w-0 py-0">
          <div className="min-w-0 p-2">
            <DataTable
              columns={columns}
              data={filteredInteractions}
              minWidth="min-w-[720px]"
              emptyMessage="Nenhum atendimento encontrado."
              pageSize={itemsPerPage}
              onRowClick={(interaction) => navigate(`/interactions/${interaction.id}`)}
            />
          </div>
        </Card>
      )}
    </div>
  )
}
